
import
{
    Alignment,
    Border,
    Cell as Cell_,
    Row,
    Workbook
} from 'exceljs';

type Cell = Cell_ & {
    materialized: boolean;
};

const rgbaToAhex = (rgbString: string) =>
{
    const regex = /rgba?\((?<r>\d+), (?<g>\d+), (?<b>\d+)(, (?<a>\d+))?\)/;
    const result = rgbString.match(regex);

    const a = parseInt(typeof result?.groups?.['a'] === 'undefined' ? '255' : result?.groups?.['a']);

    if (!a) return "";

    const r = parseInt(result?.groups?.['r'] || '0');
    const g = parseInt(result?.groups?.['g'] || '0');
    const b = parseInt(result?.groups?.['b'] || '0');

    return `${[a, r, g, b].map(v => v.toString(16).padStart(2, "0")).join('')}`;
};

const pxToPt = (px: number) => px * (72 / 96);

const pxToColWidth = (px: number) => px / 8;

const getBorder = (color: string, style: string, width: string) =>
{

    if (style === 'none') {
        return {};
    }

    const border: Border = {
        color: { argb: rgbaToAhex(color) },
        style: 'thin'
    };

    if (parseInt(width) > 2) {
        border.style = 'medium';
    }

    if (parseInt(width) > 4) {
        border.style = 'thick';
    }

    return border;

};

const formatCell = (cell: Cell, style: CSSStyleDeclaration) =>
{
    const {
        backgroundColor,
        fontFamily, fontSize, color, fontWeight,
        textAlign, verticalAlign,
        writingMode,
        borderTopColor, borderLeftColor, borderRightColor, borderBottomColor,
        borderTopStyle, borderLeftStyle, borderRightStyle, borderBottomStyle,
        borderTopWidth, borderLeftWidth, borderRightWidth, borderBottomWidth,
    } = style;

    cell.font = {
        name: fontFamily,
        bold: parseInt(fontWeight) >= 700,
        color: { argb: rgbaToAhex(color) || undefined },

        size: pxToPt(parseInt(fontSize))
    };

    const fill = rgbaToAhex(backgroundColor);

    if (fill) cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: fill }
    };

    cell.border = {
        bottom: getBorder(borderBottomColor, borderBottomStyle, borderBottomWidth),
        top: getBorder(borderTopColor, borderTopStyle, borderTopWidth),
        left: getBorder(borderLeftColor, borderLeftStyle, borderLeftWidth),
        right: getBorder(borderRightColor, borderRightStyle, borderRightWidth),
    };

    cell.alignment = {
        'horizontal': <'center' | 'left' | 'right'>textAlign,
        'vertical': <'middle' | 'top' | 'bottom'>verticalAlign,
        'textRotation': writingMode === 'sideways-lr' ? 90 : 0
    };


    return cell;

};

const mergeCells = (cell: Cell, columns: number = 1, rows: number = 1) =>
{
    if (columns > 1 || rows > 1) {

        const ws = cell.worksheet;
        const [row, col] = [parseInt(cell.row), parseInt(cell.col)];

        ws.mergeCells(row, col, row + (rows - 1), col + (columns - 1));
    }

    return cell;
};

const getNextCell = (row: Row): Cell =>
{
    for (let i = 0; i <= row.cellCount; ++i) {
        const cell: Cell = row.getCell(i + 1) as Cell;
        console.log(cell.isMerged, cell.materialized);
        if (!cell.isMerged && !cell.materialized) {
            return cell;
        }
    }


    return row.getCell(1) as Cell;

};

export class ExcelWorkBook
{

    private instance: Workbook = new Workbook();

    constructor(private fileName: string) { };

    public addSheet(table: HTMLTableElement, name?: string)
    {

        const ws = this.instance.addWorksheet(name);

        for (const tableRow of table.rows) {

            const rowIndex = tableRow.rowIndex + 1;
            const row = ws.getRow(rowIndex);

            for (const tableCell of tableRow.cells) {

                const style = getComputedStyle(tableCell);
                const { height, width } = style;
                const { rowSpan, colSpan } = tableCell;

                const cell = mergeCells(
                    formatCell(
                        getNextCell(row),
                        style),
                    colSpan, rowSpan);

                cell.value = tableCell.innerText;
                cell.materialized = true;

                const column = ws.getColumn(cell.col);

                if (!column.width && !cell.isMerged) {

                    column.width = pxToColWidth(parseFloat(width));
                    console.log(column.width, width);
                }

                if (!row.height && !cell.isMerged) {
                    row.height = pxToPt(parseFloat(height));
                }

            }

        }

        return ws;

    }

    public async download()
    {
        const buffer = await this.instance.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${this.fileName}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);

    }

}